import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../utils/api';

type PlatformIdentityDetailResponse = {
  platformIdentity: {
    id: string;
    shortId: string;
    status: 'ACTIVE' | 'BLOCKED' | 'ARCHIVED' | string;
    createdAt: string;
    updatedAt: string;
  };
  personLabel: string;
  telegramAccounts: Array<{
    id: string;
    telegramChatId: string;
    telegramUsername?: string | null;
    isPrimary: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  tennis: {
    exists: boolean;
    playerId: string | null;
    playerName: string | null;
    playerEmail: string | null;
    playerActive: boolean | null;
  };
  padel: {
    exists: boolean;
    id: string | null;
    status: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
};

const statusBadge = (status: string): string => {
  if (status === 'ACTIVE') return 'bg-green-100 text-green-800';
  if (status === 'BLOCKED') return 'bg-red-100 text-red-800';
  if (status === 'ARCHIVED') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-700';
};

const formatDateTimeRu = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const PlatformIdentityDetail: React.FC = () => {
  const { id } = useParams();
  const [data, setData] = useState<PlatformIdentityDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((current) => (current === key ? null : current));
    }, 1500);
  };

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError('Identity ID не указан');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const detail = await apiGet<PlatformIdentityDetailResponse>(`/platform-identities/${id}`);
        setData(detail);
      } catch (e: any) {
        setError(e?.message || 'Не удалось загрузить идентичность');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link to="/identities" className="text-blue-700 hover:text-blue-900 underline text-sm">
          ← К списку идентичностей
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error || 'Идентичность не найдена'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{data.personLabel}</h1>
          <p className="text-sm text-gray-500 mt-1">Идентичность: {data.platformIdentity.id}</p>
        </div>
        <Link to="/identities" className="text-blue-700 hover:text-blue-900 underline text-sm">
          ← Назад к списку
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Идентичность</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-3">
            <span>
              <span className="text-gray-500">ID:</span>{' '}
              <span className="text-gray-900">{data.platformIdentity.id}</span>
            </span>
            <button
              type="button"
              onClick={() => copyToClipboard(data.platformIdentity.id, 'identity')}
              className="text-xs text-blue-700 hover:text-blue-900 underline"
            >
              Копировать ID
            </button>
            {copiedKey === 'identity' && <span className="text-xs text-green-700">Скопировано</span>}
          </div>
          <div>
            <span className="text-gray-500">Статус:</span>{' '}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(data.platformIdentity.status)}`}>
              {data.platformIdentity.status}
            </span>
          </div>
          <div><span className="text-gray-500">Создана:</span> <span className="text-gray-900">{formatDateTimeRu(data.platformIdentity.createdAt)}</span></div>
          <div><span className="text-gray-500">Обновлена:</span> <span className="text-gray-900">{formatDateTimeRu(data.platformIdentity.updatedAt)}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Telegram-аккаунты</h2>
        {data.telegramAccounts.length === 0 ? (
          <p className="text-sm text-gray-500">—</p>
        ) : (
          <div className="space-y-3">
            {data.telegramAccounts.map((account) => (
              <div key={account.id} className="border border-gray-200 rounded-md p-3 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">{account.telegramUsername ? `@${account.telegramUsername}` : account.telegramChatId}</span>
                  {account.isPrimary && (
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">Основной</span>
                  )}
                </div>
                <div className="text-gray-600">Chat ID: {account.telegramChatId}</div>
                <div className="text-gray-500 text-xs mt-1">
                  Создан: {formatDateTimeRu(account.createdAt)} • Обновлён: {formatDateTimeRu(account.updatedAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Теннисный профиль</h2>
        {data.tennis.exists ? (
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Игрок:</span> <span className="text-gray-900">{data.tennis.playerName || '—'}</span></div>
            <div className="flex items-center gap-3">
              <span>
                <span className="text-gray-500">Player ID:</span>{' '}
                <span className="text-gray-900">{data.tennis.playerId}</span>
              </span>
              {data.tennis.playerId && (
                <>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(data.tennis.playerId as string, 'player')}
                    className="text-xs text-blue-700 hover:text-blue-900 underline"
                  >
                    Копировать ID
                  </button>
                  {copiedKey === 'player' && <span className="text-xs text-green-700">Скопировано</span>}
                </>
              )}
            </div>
            <div><span className="text-gray-500">Статус игрока:</span> <span className="text-gray-900">{data.tennis.playerActive ? 'Активен' : 'Неактивен'}</span></div>
            {data.tennis.playerId && (
              <Link to={`/players?search=${encodeURIComponent(data.tennis.playerId)}`} className="text-blue-700 hover:text-blue-900 underline">
                Открыть игрока →
              </Link>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Теннисный профиль отсутствует</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Падел-профиль</h2>
        {data.padel.exists ? (
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">ID:</span> <span className="text-gray-900">{data.padel.id}</span></div>
            <div><span className="text-gray-500">Статус:</span> <span className="text-gray-900">{data.padel.status || '—'}</span></div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Падел-профиль отсутствует</p>
        )}
      </div>
    </div>
  );
};

export default PlatformIdentityDetail;
